import { FilterButton, WideFilters } from '@/components/Filters'
import { WhoseBar, WhoseButton } from '@/components/Whose'
import { useWhosePicker } from '@/hooks/useWhosePicker'
import { Segmented } from '@/components/Segmented'
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@/components/icons'
import { fmtMonthYear, startOfWeek } from '@/logic/dates'
import { dayTitle, weekTitle } from '@/logic/labels'
import { setCalendarDate, setCalendarMode, setSearchOpen, useUi, type CalendarMode } from '@/store/ui'
import type { DateStr } from '@/types'
import { nextPeriod, prevPeriod } from './shared'

const MODES: { value: CalendarMode; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
]

interface HeaderProps {
  mode: CalendarMode
  date: DateStr
  today: DateStr
}

/**
 * The period's name over its dates: "Today" over "Wed 14 Oct", "This week"
 * over "12 – 18 Oct", "October 2026"; a day or week without a name shows just
 * its dates. Tapping it returns to today.
 */
function PeriodTitle({ mode, date, today }: HeaderProps) {
  let name: string | null
  let dates: string | null
  if (mode === 'day') {
    const t = dayTitle(date, today)
    name = t.name
    dates = t.date
  } else if (mode === 'week') {
    const t = weekTitle(startOfWeek(date), startOfWeek(today))
    name = t.name
    dates = t.span
  } else {
    name = fmtMonthYear(date)
    dates = null
  }
  return (
    <button type="button" className="ctop__period" onClick={() => setCalendarDate(today)} title="Back to today">
      {name ? (
        <>
          <b>{name}</b>
          {dates && <small>{dates}</small>}
        </>
      ) : (
        <span className="ctop__only">{dates}</span>
      )}
    </button>
  )
}

function Arrows({ mode, children }: { mode: CalendarMode; children: React.ReactNode }) {
  return (
    <>
      <button type="button" className="icon-btn" aria-label={`Previous ${mode}`} onClick={prevPeriod}>
        <ChevronLeftIcon size={18} weight={2.4} />
      </button>
      {children}
      <button type="button" className="icon-btn" aria-label={`Next ${mode}`} onClick={nextPeriod}>
        <ChevronRightIcon size={18} weight={2.4} />
      </button>
    </>
  )
}

function ModeSwitch({ mode, fill }: { mode: CalendarMode; fill: boolean }) {
  return <Segmented label="Calendar view" fill={fill} value={mode} options={MODES} onChange={setCalendarMode} />
}

function OfflineRow() {
  const online = useUi((s) => s.online)
  if (online) return null
  return <div className="ctop__offline">Offline · changes will sync when you're back online</div>
}

/** A phone's top rows: the period and its arrows, search, Whose and filters, then Day, Week and Month. */
export function CalendarTopRow({ mode, date, today }: HeaderProps) {
  const whose = useWhosePicker()
  return (
    <>
      <div className="ctop">
        <Arrows mode={mode}>
          <PeriodTitle mode={mode} date={date} today={today} />
        </Arrows>
        <span className="ctop__spacer" />
        <button type="button" className="icon-btn" aria-label="Search" onClick={() => setSearchOpen(true)}>
          <SearchIcon size={20} />
        </button>
        <WhoseButton picker={whose} />
        <FilterButton />
      </div>
      <WhoseBar picker={whose} />
      <OfflineRow />
      <div className="ctop__modes">
        <ModeSwitch mode={mode} fill />
      </div>
    </>
  )
}

/** A wide screen's row under the tabs: the period, Day, Week and Month, and the filters in view. */
export function CalendarWideRow({ mode, date, today }: HeaderProps) {
  return (
    <>
      <div className="ctop ctop--wide">
        <Arrows mode={mode}>
          <PeriodTitle mode={mode} date={date} today={today} />
        </Arrows>
        <span className="ctop__switch">
          <ModeSwitch mode={mode} fill={false} />
        </span>
        <span className="ctop__spacer" />
        <WideFilters />
      </div>
      <OfflineRow />
    </>
  )
}
